import React, { useEffect, useState } from 'react';
import AutomationsService from '../../services/automationsService';
import Layout from '../../components/Layout/Layout';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { UserState } from '../../store/reducers/userReducer';
import { Badge, FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import LcLoading from '../../components/Generic/LcLoading';
import LcInfiniteTable from '../../components/Data/LcInfiniteTable';
import "./index.css";
import { PrefState } from '../../store/reducers/prefReducer';
import LcIconLink from '../../components/Generic/LcIconLink';
import moment from 'moment';
import DialogConfirmForm from '../../components/Layout/Dialog/DialogConfirmForm';
import { useSnackbar } from 'notistack';
import { PPGrid, PPModal, PPTextArea } from 'processor-plataform-ui';
import PPDropDown from '../../components/PPDropDown';
import { VendorService } from '../../services/vendorService';
import LcToggle from '../../components/Form/LcToggle';
import LcNoData from '../../components/Generic/LcNoData';
import { HeyHoService } from '../../services/heyHoService';

const Automations: React.FC = (props) => {

	//#region DECLARATIONS
	const automationsService = new AutomationsService(props);
	const vendorService = new VendorService(props);
	const [heyhoService] = useState<HeyHoService>(new HeyHoService(props));
	const user = useSelector<RootState, UserState>(state => state.user);
	const pref = useSelector<RootState, PrefState>(state => state.pref);
	const { enqueueSnackbar } = useSnackbar();
	const dispatch = useDispatch();

	const resourceReferenceDic = {
		1: "SubscriptionId",
		2: "AccountId"
	}

	interface Scheduler {
		id?: number,
		clientGroupId: number,
		automationId: number,
		resourceId: number,
		data: SchedulerData | string,
		status: number,
		weekDaysScheduled: string,
		hourScheduled: string,
		lastRun?: string
	};

	const defaultScheduler: Scheduler = {
		id: 0,
		clientGroupId: user.ClientGroupSelected ?? 0,
		automationId: 0,
		resourceId: 0,
		data: "",
		status: 0,
		weekDaysScheduled: "",
		hourScheduled: "",
		lastRun: ""
	};

	interface SchedulerData {
		ClientName: string,
		CloudOS: string,
		Automation: string,
		Region?: string | null,
		TenantId?: string | null,
		ApplicationId?: string | null,
		SubscriptionId?: string | null,
		AccountId?: string | null,
		ResourceGroupName?: string | null,
		ResourceName?: string | null,
		ResourceId?: string | null,
		KeyVaultCredential: string,
		KeyVaultAccessKey?: string | null,
		Size?: string | null,
		AppServicePlanName?: string | null,
		WorkerSize?: string | null,
		Tier?: string | null
	};

	const defaultSchedulerData: SchedulerData = {
		ClientName: "",
		CloudOS: "",
		Automation: "",
		Region: null,
		TenantId: null,
		ApplicationId: null,
		SubscriptionId: null,
		AccountId: null,
		ResourceGroupName: null,
		ResourceName: null,
		ResourceId: null,
		KeyVaultCredential: "",
		KeyVaultAccessKey: null,
		Size: null,
		AppServicePlanName: null,
		WorkerSize: null,
		Tier: null
	};

	interface RunHistory {
		id: number;
		automationId: number;
		automationsSchedulerId: number;
		status: string;
		date: string;
		timeStarted: string;
		timeFinished: string;
		data: string;
		runId: string;
	};

	interface AuditRecord {
		clientGroupId: number,
		automationId: number,
		clientUserId: string,
		clientUserName: string,
		action: string,
		timestamp: string,
		originalValues: string,
		currentValues: string
	}

	interface Template {
		id: number;
		name: string;
		cloudOSId: number;
		dataSample: string;
		description: string;
	};

	const defaultTemplate: Template = {
		id: 0,
		name: "",
		cloudOSId: 0,
		dataSample: "{}",
		description: ""
	}

	const groupsDic = {
		VM: "VM",
		DB: "banco de dados"
	}

	interface ListOption {
		label: number | string,
		value: number | string
	};

	const defaultListOption: ListOption = {
		label: "",
		value: ""
	}

	interface DataTableFilter {
		size: number,
		column: string,
		direction: string,
		automationId: string;
		resourceId: string;
		hourScheduled: string;
		status: number;
	};

	const defaultDataTableFilter: DataTableFilter = {
		size: 20,
		column: "resourceId",
		direction: "asc",
		automationId: "",
		resourceId: "",
		hourScheduled: "0,23",
		status: 0
	};

	interface WeekDays {
		MON: boolean,
		TUE: boolean,
		WED: boolean,
		THU: boolean,
		FRI: boolean,
		SAT: boolean,
		SUN: boolean
	}

	const defaultWeekDays: WeekDays = {
		MON: false,
		TUE: false,
		WED: false,
		THU: false,
		FRI: false,
		SAT: false,
		SUN: false
	}

	interface Hour {
		hour: string,
		minute: string
	}

	const defaultHour: Hour = {
		hour: "",
		minute: ""
	}

	const semanaDic = {
		"SUN": ["dom.", "Domingo"],
		"MON": ["seg.", "Segunda"],
		"TUE": ["ter.", "Terça"],
		"WED": ["qua.", "Quarta"],
		"THU": ["qui.", "Quinta"],
		"FRI": ["sex.", "Sexta"],
		"SAT": ["sáb.", "Sábado"],
	};

	const automationsNameDic = {
		"Start RDS": "(AWS) Ligar banco de dados",
		"Stop RDS": "(AWS) Desligar banco de dados",
		"Resize RDS": "(AWS) Redimensionar banco de dados",
		"Start EC2": "(AWS) Ligar máquina virtual",
		"Stop EC2": "(AWS) Desligar máquina virtual",
		"Resize EC2": "(AWS) Redimensionar máquina virtual",
		"Start VM": "(Azure) Ligar máquina virtual",
		"Stop VM": "(Azure) Desligar máquina virtual",
		"Resize VM": "(Azure) Redimensionar máquina virtual",
		"Resize SQL Database": "(Azure) Redimensionar banco de dados",
		"Resize App Service Plan": "(Azure) Redimensionar app service plan",
	};

	const hourScheduledOptions = {
		hours: Array.from(Array(24).keys()).map(h => {
			let _h = h.toString().length > 1 ? h : `0${h}`;
			return { label: _h, value: _h }
		}),
		minutes: Array.from(Array(60).keys()).filter(h => h % 5 == 0).map(h => {
			let _h = h.toString().length > 1 ? h : `0${h}`;
			return { label: _h, value: _h }
		})
	}

	interface Resource {
		resourceName: string,
		resourceID: string,
		resourceType: string,
		resourceProvider: string,
		resourceGroup: string,
		resourceRegion: string,
		resourceSubscription: string,
		instanceSize: string
	}

	const defaultResource: Resource = {
		resourceName: "",
		resourceID: "",
		resourceType: "",
		resourceProvider: "",
		resourceGroup: "",
		resourceRegion: "",
		resourceSubscription: "",
		instanceSize: ""
	}

	interface ClientResources {
		owner: number | undefined,
		1: {
			VM: Resource[],
			DB: Resource[],
			Sizes: any[]
		},
		2: {
			VM: Resource[],
			DB: Resource[],
			Sizes: any[]
		},
	}

	const defaultClientResources: ClientResources = {
		owner: undefined,
		1: {
			VM: [],
			DB: [],
			Sizes: []
		},
		2: {
			VM: [],
			DB: [],
			Sizes: []
		},
	}

	interface ClientProviders {
		owner: number | undefined,
		Azure: any[],
		AWS: any[]
	}

	const defaultClientProviders: ClientProviders = {
		owner: undefined,
		Azure: [],
		AWS: []
	}

	interface HeyHoTicket {
		id: number,
		contrato: number,
		titulo: string,
		ServiceTree: string,
		dataInicio: string,
		heyHoId: number,
		heyHoSatatus: string,
		heyHoLink: string,
		criticidade: string,
		Descricao: string
	}

	interface HeyHoServiceGroup {
		label: string,
		value: string,
		gtmid: number
	}

	interface UserAccess {
		canListAll: boolean,
		canRequestSchedule: boolean,
		canCreateAvailability: boolean,
		canCreateResizing: boolean,
		canRemove: boolean
	}

	const userAccess: UserAccess = {
		canListAll: user.IsAdm || user.Permission.some(p =>
			p.permissoesChave == "Permite_Visualizar_Automacoes_Agendadas" && p.permissoesValor.toLowerCase() == "sim"
		),
		canRequestSchedule: !user.IsAdm && user.Permission.some(p =>
			p.permissoesChave == "Permite_Agendar_Automacoes_Via_Chamado" && p.permissoesValor.toLowerCase() == "sim"
		),
		canCreateAvailability: user.IsAdm || user.Permission.some(p =>
			p.permissoesChave == "Permite_Agendar_Automacoes_Disponibilidade" && p.permissoesValor.toLowerCase() == "sim"
		),
		canCreateResizing: user.IsAdm || user.Permission.some(p =>
			p.permissoesChave == "Permite_Agendar_Automacoes_Redimensionamento" && p.permissoesValor.toLowerCase() == "sim"
		),
		canRemove: user.IsAdm || user.Permission.some(p =>
			p.permissoesChave == "Permite_Remover_Automacoes_Agendadas" && p.permissoesValor.toLowerCase() == "sim"
		),
	}

	const isControlPlane = user.ClientGroupSelected?.toString() === "999999999";

	const [clientProviders, setClientProviders] = useState<ClientProviders>(defaultClientProviders);
	const [clientResources, setClientResources] = useState<ClientResources>(defaultClientResources);

	const [schedulers, setSchedulers] = useState<Scheduler[]>([]);
	const [filteredSchedulers, setFilteredSchedulers] = useState<Scheduler[]>([]);
	const [scheduler, setScheduler] = useState<Scheduler>(defaultScheduler);
	const [templates, setTemplates] = useState<Template[]>([]);
	const [template, setTemplate] = useState<Template>(defaultTemplate);
	const [suggestedTemplate, setSuggestedTemplate] = useState<Template>();
	const [schedulerData, setSchedulerData] = useState<SchedulerData>(defaultSchedulerData);
	const [runHistory, setRunHistory] = useState<RunHistory[]>([]);
	const [auditRecords, setAuditRecords] = useState<AuditRecord[]>([]);
	const [heyhoTicketObs, setHeyhoTicketObs] = useState<string>("");

	const [group, setGroup] = useState<"VM" | "DB">("VM");
	const [types, setTypes] = useState<ListOption[]>([]);
	const [type, setType] = useState<ListOption>(defaultListOption);
	const [resource, setResource] = useState<Resource>(defaultResource);
	const [resourceOptions, setResourceOptions] = useState<ListOption[]>([]);
	const [resourceOption, setResourceOption] = useState<ListOption>(defaultListOption);
	const [sizes, setSizes] = useState<ListOption[]>([]);
	const [size, setSize] = useState<ListOption>(defaultListOption);
	const [weekDays, setWeekDays] = useState<WeekDays>(defaultWeekDays);
	const [hour, setHour] = useState<Hour>(defaultHour);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isLoadingResources, setIsLoadingResources] = useState<boolean>(false);
	const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
	const [isLoadingAudit, setIsLoadingAudit] = useState<boolean>(false);
	const [isSendingTicket, setIsSendingTicket] = useState<boolean>(false);
	const [dataTableFilter, setDataTableFilter] = useState<DataTableFilter>(defaultDataTableFilter);
	const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
	const [showHistoricModal, setShowHistoricModal] = useState<boolean>(false);
	const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
	const [showAuditModal, setShowAuditModal] = useState<boolean>(false);
	const [showHeyhoTicketModal, setShowHeyhoTicketModal] = useState<boolean>(false);
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
	const [showToggleActiveConfirmation, setShowToggleActiveConfirmation] = useState<boolean>(false);
	const [showCreateSuggestionConfirmation, setShowCreateSuggestionConfirmation] = useState<boolean>(false);
	const [suggestionMode, setSuggestionMode] = useState<boolean>(false);
	const [showFormModal, setShowFormModal] = useState<boolean>(false);
	const [formIsValid, setFormIsValid] = useState<boolean>(false);
	const [usingResize, setUsingResize] = useState<boolean>(false);

	// Var Global p/ gerenciar o modo Sugestão
	var ignoreSuggestion = false;

	//#endregion

	const loadTableData = () => {

		const loadData = async () => {
			if (userAccess.canListAll) {
				let _schedulers = await automationsService.GetAutomations();
				setSchedulers(_schedulers.data);
			} else
				showInfo('Usuário não tem permissão para listar automações!', 'warning');

			let _templates = await automationsService.GetTemplates();
			setTemplates(_templates.data);

			await heyhoService.RetrieveContractsAndGTM();
		};

		setIsLoading(true);

		loadData()
			.catch(error => console.error(`[ERRO AUTOMAÇÕES] Carga inicial de dados: ${error}`))
			.then(() => setIsLoading(false));
	}

	useEffect(() => {

		const getSubscriptionsAndResources = async (credentials: any[], vendorId: number, providerId: number) => {
			let _subscriptions = credentials.find(sub => sub['cloudOSProviderId'] === vendorId);
			if (_subscriptions) {
				let creds = await vendorService.Retrieve(_subscriptions.id);
				let res = await automationsService.GetResources(providerId);
				let resData = res.status == 200 ? res.data : { resources: [], instanceSizes: [] };
				return {
					provider: creds.hasOwnProperty('data') ? creds['data'].vendorCredentialItem : [],
					vms: resData['resources'].filter((d: Resource) => d.resourceType == "VM" || false),
					dbs: resData['resources'].filter((d: Resource) => d.resourceType == "DB" || false),
					sizes: resData['instanceSizes']
				}
			}

			return { provider: [], vms: [], dbs: [], sizes: [] }
		};

		const handleException = (error: any) => {
			console.error(`[ERRO AUTOMACOES] Carga inicial de recursos: ${error}`);
			showInfo(`Erro ao carregar os recursos! Atualize a página para tentar novamente.`, `error`);
			setIsLoadingResources(false);
		}

		const needReload = (clientProviders.owner !== user.ClientGroupSelected || clientResources.owner !== user.ClientGroupSelected)
			|| (clientProviders.Azure.length == 0 || clientResources[1].VM.length == 0 || clientResources[1].DB.length == 0)
			|| (clientProviders.AWS.length == 0 || clientResources[2].VM.length == 0 || clientResources[2].DB.length == 0)

		if (!isControlPlane && needReload) {
			setIsLoadingResources(true);
			showInfo(`Carregando recursos...`, `warning`);
			vendorService.RetrieveList()
				.then(subscriptions => {

					const azureTask = () => getSubscriptionsAndResources(subscriptions.data.dataCollection, 8, 1);
					const awsTask = () => getSubscriptionsAndResources(subscriptions.data.dataCollection, 2, 2);

					Promise.all([azureTask(), awsTask()])
						.then(([azure, aws]) => {
							setClientProviders({ owner: user.ClientGroupSelected, Azure: azure.provider, AWS: aws.provider });
							setClientResources({
								owner: user.ClientGroupSelected,
								1: { VM: azure.vms, DB: azure.dbs, Sizes: azure.sizes },
								2: { VM: aws.vms, DB: aws.dbs, Sizes: aws.sizes }
							});
							showInfo(`Recursos carregados!`, `success`);
							setIsLoadingResources(false);
						})
						.catch(error => handleException(error));
				})
				.catch(error => handleException(error));
		}

	}, [user.ClientGroupSelected]);

	const filterTableData = () => {
		let filtered = [...schedulers];

		let { automationId, resourceId, hourScheduled, status } = dataTableFilter;

		let notEmpty = automationId !== defaultDataTableFilter.automationId
			|| resourceId !== defaultDataTableFilter.resourceId
			|| hourScheduled !== defaultDataTableFilter.hourScheduled
			|| status !== defaultDataTableFilter.status;

		if (notEmpty) {
			if (automationId !== "")
				filtered = filtered.filter((p: Scheduler) => {
					let desc = templates.find(t => t.id == p.automationId)?.description.toLowerCase()!;
					return desc.indexOf(automationId) > -1
				})
			if (resourceId !== "")
				filtered = filtered.filter((p: Scheduler) => {
					let data = JSON.parse(p.data as string);
					let resource = data.ResourceId ?? data.ResourceName;
					return resource.toLowerCase().indexOf(resourceId) > -1
				})
			if (hourScheduled !== "") {
				let aHorario = hourScheduled.split(",").map((v: string, i: number) => i == 0 ? `${v}:00` : `${v}:59`)
				filtered = filtered.filter((p: Scheduler) => moment(p.hourScheduled, "hh:mm")
					.isBetween(moment(aHorario[0], "hh:mm"), moment(aHorario[1], "hh:mm"), null, '[]')
				);
			}
			if (!isNaN(status) && status > 0)
				filtered = filtered.filter((p: Scheduler) => {
					let cond = [true, p.status == 0, p.status >= 1];
					return cond[status];
				});
		}

		filtered = filtered.sort((a: any, b: any) => {
			if (dataTableFilter.direction == 'asc') return (a[dataTableFilter.column] > b[dataTableFilter.column]) ? 1 : -1;
			else return (a[dataTableFilter.column] > b[dataTableFilter.column]) ? -1 : 1;
		});
		setFilteredSchedulers(filtered);
	}

	const loadAutomationRunHistory = () => {
		if (scheduler && scheduler.id! > 0 && showHistoricModal) {
			setIsLoadingHistory(true);
			automationsService.GetAutomationRunHistory(scheduler.id!)
				.then(result => setRunHistory(
					result.data.sort((a, b) => a["date"] > b["date"] ? -1 : 1)
				))
				.catch(error => console.error(error))
				.then(() => setIsLoadingHistory(false))
		}
	}

	const loadAutomationAuditRecords = () => {
		if (scheduler && scheduler.id! > 0 && showAuditModal) {
			setIsLoadingAudit(true);
			automationsService.GetAuditRecords(scheduler.id!)
				.then(result => {
					if (result.status == 204)
						result.data = [];

					setAuditRecords(
						result.data.sort((a, b) => moment(a["timestamp"]) < moment(b["timestamp"]) ? -1 : 1)
					)
				})
				.catch(error => console.error(error))
				.then(() => setIsLoadingAudit(false))
		}
	}

	const toggleHistoricModal = (id: number) => {
		if (!showHistoricModal)
			readCurrent(id);

		setShowHistoricModal(!showHistoricModal);
	}

	const toggleAuditModal = (id: number) => {
		if (!showAuditModal)
			readCurrent(id);

		setShowAuditModal(!showAuditModal);
	}

	const toggleFormModal = (id?: number) => {
		id && !showFormModal ? readCurrent(id) : startNew();
		setShowFormModal(!showFormModal)
		setSuggestionMode(false);
	}

	const toggleDetailModal = (id: number) => {
		if (!showDetailsModal)
			readCurrent(id);

		setShowDetailsModal(!showDetailsModal);
	}

	const toggleTicketModal = () => {
		setHeyhoTicketObs("");
		setShowHeyhoTicketModal(!showHeyhoTicketModal);
	}

	const readCurrent = (id: number) => {
		let _current: Scheduler = schedulers.find(a => a.id == id) ?? defaultScheduler;
		setScheduler(_current);

		let _data: SchedulerData = JSON.parse(_current.data as string);
		setSchedulerData(_data);

		let _template: Template = templates.find(at => at.id == _current.automationId) ?? defaultTemplate;
		setTemplate(_template);

		let _group = Object.keys(groupsDic).find(g => _template.description.indexOf(groupsDic[g]) > -1)! as "VM" | "DB";
		setGroup(_group);

		let _types = filterPermittedTemplatesByGroup(groupsDic[_group]) as ListOption[];
		setTypes(_types);

		let _type = { label: automationsNameDic[_template.name], value: _template.id } as ListOption;
		setType(_type);

		let _resource: Resource = clientResources[_template.cloudOSId][_group]
			.find((cr: Resource) => {
				let _clientResource = { 1: _data.ResourceName, 2: _data.ResourceId };
				let _comparedResource = { 1: cr.resourceName, 2: _group == "VM" ? cr.resourceID : cr.resourceName };
				return _clientResource[_template.cloudOSId] == _comparedResource[_template.cloudOSId]
			}) ?? defaultResource;
		setResource(_resource);

		let _resourceOptions = clientResources[_template.cloudOSId][_group].map((r: Resource) => {
			return { label: r.resourceName, value: r.resourceID } as ListOption
		}) as ListOption[] ?? [];
		setResourceOptions(_resourceOptions);

		let _resourceOption: ListOption = { label: _resource.resourceName, value: _resource.resourceID };
		if (_resourceOption.label == "")
			_resourceOption.label = "Recurso não encontrado! Considere recriar esta automação.";
		setResourceOption(_resourceOption);

		let _usingResize = _data.hasOwnProperty('Size') && _data.Size !== "";
		setUsingResize(_usingResize);

		let _hasSizesForRegion = Object.keys(clientResources[_template.cloudOSId].Sizes).length > 0
			&& clientResources[_template.cloudOSId].Sizes.hasOwnProperty(_resource.resourceRegion);
		if (_hasSizesForRegion) {
			let _allSizes = clientResources[_template.cloudOSId].Sizes[_resource.resourceRegion];
			let _filteredSizes = _template.cloudOSId == 2 ? _allSizes.filter(s =>
				(_group == "VM" && s.indexOf("db") != 0) || (_group == "DB" && s.indexOf("db") == 0)
			) : _allSizes;
			let _sizes = _filteredSizes.map((isa: string) => { return { label: isa, value: isa } as ListOption });
			setSizes(_sizes);

			let _sizeInfo = _data.Size ?? _resource.instanceSize ?? "";
			let _size: ListOption = _usingResize ? { label: _sizeInfo, value: _sizeInfo } : _sizes[0];
			setSize(_size);
		}

		let _weekDays: WeekDays = defaultWeekDays;
		Object.keys(defaultWeekDays).forEach(day => _weekDays[day] = _current.weekDaysScheduled.indexOf(day) > -1);
		setWeekDays(_weekDays);

		let hourString = moment(_current.hourScheduled, 'HH:mm:ss').format('HH:mm');
		let [__hour = '00', __minute = '00'] = hourString.split(":");
		let _hour: Hour = { hour: __hour ?? '00', minute: __minute ?? '00' }
		setHour(_hour);
	}

	const startNew = () => {
		setGroup("VM");
		let _types = filterPermittedTemplatesByGroup("VM");
		setTypes(_types);
		setType(_types[0]);
		setSizes([defaultListOption]);
		setSize(defaultListOption);
		setResource(defaultResource);
		setWeekDays(defaultWeekDays);
		setHour({ hour: "", minute: "" });
		let _template = templates.find(t => t.id == _types[0].value) ?? defaultTemplate;
		setSchedulerData(JSON.parse(_template?.dataSample!) as SchedulerData);
		setScheduler(defaultScheduler);
		setTemplate(_template);
	}

	const handleGroupChange = () => {
		if (group) {
			let _types = filterPermittedTemplatesByGroup(groupsDic[group]);
			setTypes(_types);

			let _type = _types.map(t => t.value).includes(type.value) ? type : _types[0];
			setType(_type);
		}
	}

	const handleTypeChange = () => {
		if (scheduler.id === 0) {
			if (!suggestionMode)
				setResourceOption(defaultListOption);

			let _template = templates.find(t => t.id === type.value) ?? defaultTemplate;
			setTemplate(_template);

			if (_template.cloudOSId == 0)
				return;

			let _data = JSON.parse(_template.dataSample) ?? defaultSchedulerData;
			setSchedulerData(_data);

			if (clientResources[_template.cloudOSId][group]) {
				let _resourceOptions = clientResources[_template.cloudOSId][group].map((r: Resource) => {
					return { label: r.resourceName, value: r.resourceID } as ListOption
				}) as ListOption[] ?? defaultListOption;
				setResourceOptions(_resourceOptions);
			}

			let _usingResize = _template.name.indexOf("Resize") > -1;
			setUsingResize(_usingResize);

			// Verifica se é uma automação "Stop" para sugerir outra "Start"
			setSuggestedTemplate(undefined);
			if (_template.name.indexOf("Stop") > -1) {
				let _pairedTemplate = _template.name.replace("Stop", "Start") as string;
				let _suggestedTemplate = templates.find(t =>
					_pairedTemplate &&
					t.name.indexOf(_pairedTemplate) > -1 &&
					t.cloudOSId == _template.cloudOSId
				);

				if (scheduler.id === 0)
					setSuggestedTemplate(_suggestedTemplate);
			}
		}

	}

	const handleResourceOptionChange = () => {
		if (template.cloudOSId > 0 && !suggestionMode) {
			setSizes([]);
			setSize(defaultListOption);

			let _selectedResource: Resource = clientResources[template.cloudOSId][group]
				.find((r: Resource) => r.resourceID == resourceOption.value) ?? defaultResource;
			setResource(_selectedResource);

			let _hasSizesForRegion = Object.keys(clientResources[template.cloudOSId].Sizes).length > 0
				&& clientResources[template.cloudOSId].Sizes.hasOwnProperty(_selectedResource.resourceRegion);

			if (_hasSizesForRegion) {
				let _allSizes = clientResources[template.cloudOSId].Sizes[_selectedResource.resourceRegion];
				let _filteredSizes = template.cloudOSId == 2 ? _allSizes.filter(s =>
					(group == "VM" && s.indexOf("db") != 0) || (group == "DB" && s.indexOf("db") == 0)
				) : _allSizes;
				let _sizes = _filteredSizes.map((isa: string) => { return { label: isa, value: isa } as ListOption });
				setSizes(_sizes);

				let _sizeInfo = schedulerData.Size ?? _selectedResource.instanceSize ?? "";
				let _size: ListOption = { label: _sizeInfo, value: _sizeInfo };
				setSize(_size);
			}
		}
	}

	const filterPermittedTemplatesByGroup = (group: string) => {
		let _filteredByGroup = [...templates]
			.filter(at => {
				let groupMatch = at.description.indexOf(group) > -1
				let azureCheck = at.cloudOSId == 1 && clientProviders.Azure.length > 0
				let awsCheck = at.cloudOSId == 2 && clientProviders.AWS.length > 0
				return groupMatch && (azureCheck || awsCheck)
			});

		let _filteredByPermission: Template[] = [];

		if (!userAccess.canRequestSchedule) {
			if (userAccess.canCreateAvailability)
				_filteredByPermission = _filteredByPermission.concat(
					_filteredByGroup.filter(t => t.name.indexOf("Start") > -1 || t.name.indexOf("Stop") > -1)
				);

			if (userAccess.canCreateResizing)
				_filteredByPermission = _filteredByPermission.concat(
					_filteredByGroup.filter(t => t.name.indexOf("Resize") > -1)
				);
		} else
			_filteredByPermission = _filteredByGroup;

		return _filteredByPermission.map(at => {
			return { label: automationsNameDic[at.name] as string, value: at.id } as ListOption
		});
	}

	const generalDisabled = {
		create: isControlPlane || isLoadingResources,
		edit: isControlPlane || isLoadingResources || (!userAccess.canCreateAvailability && !userAccess.canCreateResizing),
		remove: isControlPlane || isLoadingResources || !userAccess.canRemove,
		toggleStatus: isControlPlane || isLoadingResources || (!userAccess.canCreateAvailability && !userAccess.canCreateResizing),
		viewDetails: !isControlPlane && !isLoadingResources && userAccess.canListAll,
	}

	const formDisabled = {
		group: suggestionMode || scheduler.id! > 0,
		type: suggestionMode || scheduler.id! > 0,
		resource: suggestionMode || scheduler.id! > 0,
		size: sizes.length == 0,
		weekDay: false,
		hour: false
	}

	const confirmDelete = (id: number) => {
		readCurrent(id);
		setShowDeleteConfirmation(true);
	}

	const deleteAutomation = (confirm: boolean) => {
		let id = scheduler?.id!;

		setShowDeleteConfirmation(false);

		if (!confirm) return;

		showInfo(`Excluindo automação...`, 'warning');

		automationsService.RemoveAutomations(id)
			.then(result => {
				if (result.status == 200)
					showInfo(`Automação excluída!`, 'success');
			})
			.catch(error => {
				console.error(error);
				showInfo(`Error ao excluir automação.`, 'error');
			})
			.then(() => {
				setScheduler(defaultScheduler);
				loadTableData();
			})
	}

	const translateAutomationWeekDays = (days: string[]): object => {
		let automationDays = Object.keys(semanaDic);
		let translated = {};

		automationDays.forEach(day => translated[semanaDic[day][0]] = days.includes(day) ? "1" : "0");

		return translated;
	}

	const renderAutomationStatus = (status: number) => {
		let content = ["Desabilitada", "Habilitada"];
		let color = ["text-danger", "text-success"];

		return (<div className={status <= 1 ? color[status] : color[1]} style={{ marginLeft: "22px" }}>{`${status <= 1 ? content[status] : content[1]}`}</div>)
	}

	const renderAuditModifications = (row: AuditRecord) => {

		var _values: any = {};
		var _timestamp = row.timestamp && moment(row.timestamp).format("DD/MM/YYYY HH:mm:ss") || "Não informado";

		if (row.hasOwnProperty("currentValues") && row.currentValues !== "") {

			// let _original = JSON.parse(row.originalValues);
			let _current = JSON.parse(row.currentValues);

			_values = {
				"Grupo": { VM: "Máquinas virtuais", DB: "Bancos de dados" }[group],
				"Automação": automationsNameDic[template.name],
				"Recurso": extractResourceName(_current.Data as string),
				"Tamanho": template.name.indexOf("Resize") > -1 ? extractSize(_current.Data as string) : "Sem Informações",
				"Dias da Semana": _current.WeekDaysScheduled.split('-').map(d => semanaDic[d][0]).toString(),
				"Horário": moment(_current.HourScheduled, "HH:mm:ss").format("HH:mm"),
				"Status": ["Desabilitada", "Habilitada"][_current.Status]
			};

			if (row.action == "UPDATE") {
				delete _values["Grupo"];
				delete _values["Automação"];
				delete _values["Recurso"];
			}
		}

		return (
			<div className="auditInfoContainer">
				<span className="auditTimestamp">{_timestamp}</span>
				{
					Object.keys(_values).length > 0 &&
					<div className="auditInfo">
						<div className="auditInfoLabels">
							{Object.keys(_values).map(v => <p>{v}:</p>)}
						</div>
						<div className="auditInfoLabels">
							{Object.keys(_values).map(v => <p>{_values[v]}</p>)}
						</div>
					</div>
				}
			</div>
		)
	}

	const extractResourceName = (data: string) => {
		let json = JSON.parse(data);
		return json.ResourceName ?? json.ResourceId;
	}

	const extractSize = (data: string) => {
		let json = JSON.parse(data);
		return json.Size;
	}

	const validateAutomationForm = () => {
		let validations = {
			TypeSelected: type && type.value as number > 0,
			ResourceSelected: resourceOption.value !== "",
			AtLeastOneWeekDay: Object.keys(weekDays).some(w => weekDays[w]),
			HourSelected: hour.hour !== "" && hour.minute !== "",
			SizeNotUsedOrSelected: !usingResize || (usingResize && size.value !== "")
		}
		setFormIsValid(Object.values(validations).reduce((all, next) => all && next, true));
	}

	const processAutomationToSend = (): { _automation: Scheduler, _ticket: HeyHoTicket } | string => {
		let _current = { ...scheduler };
		let _template = { ...template };

		if (!_template)
			return "Automação base inválida!";

		if (_current.id == 0)
			delete _current.id;
		delete _current.lastRun;
		_current.automationId = _template.id;
		_current.data = { ...schedulerData };
		_current.hourScheduled = `${hour.hour}:${hour.minute}:00`;
		_current.weekDaysScheduled = Object.keys(weekDays).filter(w => weekDays[w] === true).join('-');
		_current.status = 1;

		let _vendorKeys = clientProviders[_current.data.CloudOS]
			.filter(v => v.active == true && v.isDirty == false)
			.map(v => JSON.parse(v.keysSecret));

		if (_vendorKeys.length == 0) return "Assinatura não encontrada para o provedor!";

		// Se houver subscriptions idênticas, pega a com ID maior (mais recente)
		let _key = _vendorKeys
			.filter(vk => vk[resourceReferenceDic[_template.cloudOSId]] == resource.resourceSubscription)
			.sort((a, b) => a.id > b.id ? 1 : -1)[0];

		let _fullData = { ...defaultSchedulerData };

		_fullData.ClientName = schedulerData.ClientName == "" ?
			toPascalCase(user.ClientGroupSelectedDescription!) :
			schedulerData.ClientName;
		_fullData.CloudOS = _current.data.CloudOS;
		_fullData.Automation = _current.data.Automation;
		_fullData.Region = resource.resourceRegion;
		_fullData.TenantId = _key.TenantId ?? "";
		_fullData.ApplicationId = _key.ApplicationId ?? "";
		_fullData.SubscriptionId = _key.SubscriptionId ?? "";
		_fullData.AccountId = resource.resourceSubscription;
		_fullData.ResourceGroupName = resource.resourceGroup;
		_fullData.ResourceName = resource.resourceName;
		_fullData.ResourceId = resource.resourceType == "DB" && _template.cloudOSId == 2 ?
			resource.resourceName : resource.resourceID;
		_fullData.KeyVaultCredential = _key.Key ?? _key.SecretKey ?? "";
		_fullData.KeyVaultAccessKey = _key.AccessKey ?? "";
		_fullData.Size = size.value as string ?? "";
		_fullData.AppServicePlanName = null;
		_fullData.WorkerSize = null;
		_fullData.Tier = null;

		// Filtra propriedades inexistentes no dataSample
		Object.keys(_fullData).forEach(field => {
			if (_current.data.hasOwnProperty(field)) {
				_current.data[field] = _fullData[field];
			} else return;
		});

		// Preenche os campos do ticket HeyHo
		let _service = "253_3093_223";
		let _svcTree: HeyHoServiceGroup[] = heyhoService.state.data.chamadoTree;
		if (userAccess.canRequestSchedule && _svcTree.length == 0) return "Falha ao ler grupo de serviços HeyHo!";
		let _svcGroup = _svcTree.find((ct: HeyHoServiceGroup) => ct.value.indexOf(_service) > -1);
		if (_svcGroup === undefined)
			_svcGroup = _svcTree[0];

		let _ticketContract = _key.DefaultContract;
		let _ticketSvcTree = _svcGroup.value;
		let _ticketDays = Object.keys(weekDays).filter(d => weekDays[d]).map(d => semanaDic[d][1].toString());
		let _ticketHours = `${hour.hour}:${hour.minute}`;
		let _ticketDesc =
			`Automação: ${_fullData.Automation}\nDias: ${_ticketDays}\nHorário: ${_ticketHours}\nObservações: ${heyhoTicketObs}`;
		let _ticketRequest: HeyHoTicket = {
			id: 0,
			contrato: _ticketContract,
			titulo: "Agendamento de Automação",
			ServiceTree: _ticketSvcTree,
			dataInicio: "",
			heyHoId: 0,
			heyHoSatatus: "Nova",
			heyHoLink: "",
			criticidade: "Normal",
			Descricao: _ticketDesc
		}

		_current.data = JSON.stringify(_current.data);

		return {
			_automation: _current,
			_ticket: _ticketRequest
		};
	}

	const sendTicket = async (ticket: HeyHoTicket) => {
		setIsSendingTicket(true);

		try {
			showInfo('Enviando solicitação, aguarde...', 'info');
			let result = await heyhoService.Save(ticket);
			// let result = { status: 200, data: { id: 123456 } };
			// console.log(ticket);

			if (result.status == 200)
				showInfo(`Solicitação ${result.data.id} criado com sucesso.`, 'success');
		} catch (e) {
			console.error(e);
			showInfo(`Erro ao tentar criar solicitação!`, 'error');
		}

		setIsSendingTicket(false);
		toggleTicketModal();
	}

	const saveAutomation = async () => {
		const _msgSet = {
			create: {
				success: "Automação criada com sucesso!",
				info: "Criando automação, aguarde...",
				error: "Erro ao criar automação: ",
				warning: "Falha ao tentar criar automação!",
			},
			update: {
				success: "Automação atualizada com sucesso!",
				info: "Atualizando automação, aguarde...",
				error: "Erro ao atualizar automação: ",
				warning: "Falha ao tentar atualizar automação!",
			},
		};

		const _send = {
			create: async (a: Scheduler) => await automationsService.CreateAutomation(a),
			update: async (a: Scheduler) => await automationsService.UpdateAutomation(a)
		}

		const _requestedScheduling = userAccess.canRequestSchedule &&
			!userAccess.canCreateAvailability && !userAccess.canCreateResizing;
		const _directAvailabilityScheduling = userAccess.canCreateAvailability &&
			(template.name.indexOf("Start") > -1 || template.name.indexOf("Stop") > -1);
		const _directResizingScheduling = userAccess.canCreateResizing &&
			template.name.indexOf("Resize") > -1;

		const _action = scheduler.id! > 0 ? "update" : "create";

		try {
			let _processed = processAutomationToSend();

			if (typeof _processed === "string")
				showInfo(`${_msgSet[_action]["error"]}: ${_processed}`, "error");

			if (typeof _processed === "object") {
				let { _automation, _ticket } = _processed;

				if (_requestedScheduling) {
					await sendTicket(_ticket);
				} else if (_directAvailabilityScheduling || _directResizingScheduling) {
					showInfo(_msgSet[_action]["info"], "info");

					let result = await _send[_action](_automation);
					// let result = { status: 200 };
					// console.log(_automation);
					if (result.status == 200)
						showInfo(`${_msgSet[_action]["success"]}`, "success");

					loadTableData();
				} else
					showInfo(`Sem permissão para criar/solicitar agendamentos!`, 'warning');
			}

			toggleFormModal();

			if (!ignoreSuggestion && suggestedTemplate) {
				setSuggestionMode(true);
				setScheduler(defaultScheduler);

				let _template = { ...suggestedTemplate };
				setTemplate(_template);

				setSchedulerData(JSON.parse(_template.dataSample));

				setGroup(group);
				setType({ label: automationsNameDic[_template.name], value: _template.id });
				setResource(resource);
				setResourceOption({ label: resource.resourceName, value: resource.resourceID });
				setWeekDays(defaultWeekDays);
				setHour(defaultHour);

				setShowFormModal(true);
			}
		} catch (ex) {
			console.error(ex);
			showInfo(`${_msgSet[_action]["warning"]}`, "warning");
		}
	}

	const acceptAutomationSuggestion = (confirm: boolean) => {
		if (!confirm) {
			setSuggestedTemplate(undefined);
			ignoreSuggestion = true;
		} else
			ignoreSuggestion = false;

		if (userAccess.canRequestSchedule)
			setShowHeyhoTicketModal(true);
		else if (userAccess.canCreateAvailability || userAccess.canCreateAvailability)
			saveAutomation();

		setShowCreateSuggestionConfirmation(false);
	}

	const confirmToggleStatus = (id: number) => {
		readCurrent(id);
		setShowToggleActiveConfirmation(true);
	}

	const toggleAutomationStatus = (confirm: boolean) => {
		setShowToggleActiveConfirmation(false);

		if (!confirm) return;

		let _current = { ...scheduler };
		let _status = _current.status;
		_current.status = _status == 0 ? 1 : 0;
		delete _current.lastRun;

		let msgSet = [
			{ loading: "Ativando", error: "ativar", success: "ativada" },
			{ loading: "Desativando", error: "desativar", success: "desativada" },
		];

		showInfo(` ${msgSet[_status].loading} automação...`, 'warning');

		automationsService.UpdateAutomation(_current)
			.then(result => {
				if (result.status == 200)
					showInfo(`Automação ${msgSet[_status].success}!`, 'success');
			})
			.catch(error => {
				console.error(error);
				showInfo(`Error ao ${msgSet[_status].error} automação.`, 'error');
			})
			.then(() => {
				setScheduler(defaultScheduler);
				loadTableData();
			})
	}

	const toPascalCase = (str: string) => {
		return `${str}`
			.toLowerCase()
			.replace(new RegExp(/[-_]+/, 'g'), ' ')
			.replace(new RegExp(/[^\w\s]/, 'g'), '')
			.replace(
				new RegExp(/\s+(.)(\w*)/, 'g'),
				($1, $2, $3) => `${$2.toUpperCase() + $3}`
			)
			.replace(new RegExp(/\w/), s => s.toUpperCase());
	}

	const showInfo = (msg: string, type: 'default' | 'error' | 'success' | 'warning' | 'info') => {
		enqueueSnackbar(msg, { variant: type, preventDuplicate: true, persist: false });
	}

	const onAutomationsSortChange = (sortData: any) => {
		const { sort, size } = sortData;
		setDataTableFilter({ ...dataTableFilter, column: sort.column, direction: sort.direction, size: size })
	};

	const onHistoricSortChange = (sortData: any) => {
		const { sort, size } = sortData;
		setRunHistory(runHistory.sort((a: any, b: any) => {
			if (sort.direction == 'asc') return (a[sort.column] > b[sort.column]) ? 1 : -1;
			else return (a[sort.column] > b[sort.column]) ? -1 : 1;
		}));
	};

	const onAuditSortChange = (sortData: any) => {
		const { sort, size } = sortData;
		setAuditRecords(auditRecords.sort((a: any, b: any) => {
			if (sort.direction == 'asc') return (a[sort.column] > b[sort.column]) ? 1 : -1;
			else return (a[sort.column] > b[sort.column]) ? -1 : 1;
		}));
	};

	const changeDensity = (density: string) => {
		dispatch({ type: 'PREF_UPDATE', payload: { name: "density", value: density } });
	}

	const functionsGeneric = [
		{
			icon: 'ppi ppi-plus',
			tooltip: isLoadingResources ? 'Aguarde o carregamento dos recursos!' :
				(userAccess.canCreateAvailability || userAccess.canCreateResizing ? "Criar automação" : "Solicitar automação"),
			onClick: toggleFormModal,
			disabled: generalDisabled.create
		}
	];

	const functionsRow = [
		{
			label: 'Auditoria',
			func: (row: any) => toggleAuditModal(row.id!)
		},
		{
			label: 'Excluir',
			func: (row: any) => confirmDelete(row.id!),
			disabled: () => generalDisabled.remove
		}
	]

	const gridFilter = {
		toggleVisibility: () => setShowFilterModal(!showFilterModal),
		size: filteredSchedulers.length
	};

	const layoutDots = (
		<>
			<div className='item' style={{ color: pref.data?.density == "high" ? "#abaeb9" : "#000" }} onClick={() => changeDensity("high")}><i className='ppi ppi-view-list' /> Densidade alta</div>
			<div className='item' style={{ color: pref.data?.density == "medium" ? "#abaeb9" : "#000" }} onClick={() => changeDensity("medium")}><i className='ppi ppi-menu' /> Densidade média</div>
			<div className='item' style={{ color: pref.data?.density == "low" ? "#abaeb9" : "#000" }} onClick={() => changeDensity("low")}><i className='ppi ppi-menu-alt-4' /> Densidade baixa</div>
		</>
	)

	const columns = [
		{
			field: 'automationId', headerName: 'Automação', width: "350px", align: "left",
			renderCell: (row: Scheduler) => automationsNameDic[templates.find(tpl => tpl.id == row.automationId)?.name!] ?? `Automação ${row.automationId}`
		},
		{
			field: 'resourceId', headerName: 'Recurso', width: "300px", align: "left",
			renderCell: (row: Scheduler) =>
				<span className={isLoadingResources ? '' : "detailLink"} onClick={() => generalDisabled.viewDetails && toggleDetailModal(row.id!)} style={{ marginLeft: "5px" }}>
					{extractResourceName(row.data as string)}
				</span>
		},
		{
			field: 'weekDaysScheduled', headerName: 'Dias da Semana', width: "400px", sort: false, overflow: "visible", align: "left",
			renderCell: (row: Scheduler) => {
				let translated = translateAutomationWeekDays(row.weekDaysScheduled.split("-"))
				return (
					<div style={{ width: "300px", display: "flex", marginLeft: "30px" }}>
						{Object.keys(translated).map((dia, i) => {
							return (
								<div key={i}>
									<Badge className={"customBadgeWeek"} badgeContent={dia} color={translated[dia] == "1" ? "primary" : "default"} />
									<br></br>
								</div>
							)
						})}
					</div>
				)
			},
		},
		{
			field: 'hourScheduled', headerName: 'Horário de Início', width: "200px", align: "left",
			renderCell: (row: Scheduler) => <div style={{ marginLeft: "15px" }}>{moment(row.hourScheduled, "HH:mm:ss").format("HH:mm")}</div>
		},
		{
			field: 'lastRun', headerName: 'Última Execução', width: "200px", overflow: "hidden", align: "left",
			renderCell: (row: Scheduler) => <div style={{ marginLeft: "20px" }}>{row.lastRun}</div>
		},
		{
			field: 'status', headerName: 'Status', width: "200px", overflow: "visible", align: "left",
			renderCell: (row: Scheduler) => renderAutomationStatus(row.status)
		},
		{
			field: 'id', width: "200px", overflow: "visible",
			renderCell: (row: Scheduler) => {
				return (
					<>
						<LcIconLink
							icon="ppi ppi-pencil"
							size='small'
							tooltipPosition='right'
							tooltip={isLoadingResources ? 'Aguarde o carregamento dos recursos!' : "Editar"}
							onClick={() => toggleFormModal(row.id!)}
							disabled={generalDisabled.edit}
						/>

						<LcIconLink
							icon="ppi ppi-restore"
							size='small'
							tooltipPosition='right'
							tooltip="Histórico"
							onClick={() => toggleHistoricModal(row.id!)}
						/>

						<LcIconLink
							icon={`ppi ppi-${row.status == 0 ? "check-circle text-success" : "ban text-danger"}`}
							size='small'
							tooltipPosition='right'
							tooltip={`${row.status == 0 ? "Ativar" : "Desativar"}`}
							onClick={() => confirmToggleStatus(row.id!)}
							disabled={generalDisabled.toggleStatus}
						/>
					</>
				)
			}
		},
	];

	const formFunctions = [
		{
			label: userAccess.canRequestSchedule ? "Solicitar automação" :
				(scheduler.id && scheduler.id > 0 ? "Salvar alterações" : "Criar automação"),
			onClick: () => {
				if (suggestedTemplate)
					setShowCreateSuggestionConfirmation(true);
				else if (userAccess.canRequestSchedule)
					setShowHeyhoTicketModal(true);
				else if (userAccess.canCreateAvailability || userAccess.canCreateResizing)
					saveAutomation();
				else return;
			},
			disabled: !formIsValid,
			hidden: false
		}
	];

	const historicColumns = [
		{ field: 'runId', headerName: 'ID da Execução', width: "35%" },
		{ field: 'date', headerName: 'Data', width: "35%", renderCell: (row: RunHistory) => moment(row.date).format("DD/MM/YYYY") },
		{
			field: 'timeStarted', headerName: 'Início', width: "35%",
			renderCell: (row: RunHistory) => row.timeStarted && moment(row.timeStarted, 'HH:mm:ss.SSS').format("HH:mm:ss") || "Não informado"
		},
		{
			field: 'timeFinished', headerName: 'Fim', width: "35%",
			renderCell: (row: RunHistory) => row.timeFinished && moment(row.timeFinished, 'HH:mm:ss.SSS').format("HH:mm:ss") || "Não informado"
		},
		{ field: 'status', headerName: 'Status', width: "35%", renderCell: (row: RunHistory) => row.status.trim() }
	];

	const historicFunctions = [
		// {
		// 	label: "Fechar",
		// 	onClick: toggleHistoricModal,
		// 	disabled: false,
		// 	hidden: false
		// }
	];

	const auditColumns = [
		{ field: 'version', headerName: "Versão", width: "5%", renderCell: (row: AuditRecord) => `${auditRecords.indexOf(row) + 1}.0` },
		{ field: 'automation', headerName: "Automação", width: "20%", renderCell: (row: AuditRecord) => automationsNameDic[template.name] },
		{ field: 'resource', headerName: "Recurso", width: "15%", renderCell: (row: AuditRecord) => resource.resourceName },
		{ field: 'clientUserName', headerName: 'Modificado Por', width: "20%" },
		{ field: 'modification', headerName: 'Modificado Em', width: "35%", renderCell: (row: AuditRecord) => renderAuditModifications(row) }
	];

	const auditFunctions = [
		{
			label: "Fechar",
			onClick: toggleAuditModal,
			disabled: false,
			hidden: false
		}
	];

	const ticketFunctions = [
		{
			label: "Solicitar",
			onClick: saveAutomation,
			disabled: false,
			hidden: false
		},
		{
			label: "Voltar",
			onClick: toggleTicketModal,
			disabled: false,
			hidden: false
		},
	];

	const tableFilter = {
		fields: [
			{ label: 'Automação', name: 'automationId', type: 'text' },
			{ label: 'Recurso', name: 'resourceId', type: 'text' },
			{ label: 'Horário (De 00:00 às 23:59)', name: 'hourScheduled', type: 'interval', min: 0, max: 23 },
			{
				label: 'Status', name: 'status', type: 'select', topDropdown: true, options: [
					{ value: 0, name: 'Todos' },
					{ value: 1, name: 'Desabilitadas' },
					{ value: 2, name: 'Habilitadas' },
				]
			},

		],
		onChange: (_filter: any, size: number) => setDataTableFilter({
			...dataTableFilter,
			size: size,
			automationId: _filter[0].value.toLowerCase(),
			resourceId: _filter[1].value.toLowerCase(),
			hourScheduled: _filter[2].value,
			status: parseInt(_filter[3].value)
		}),
		visible: showFilterModal,
		onChangeVisibility: () => setShowFilterModal(!showFilterModal)
	}

	useEffect(loadTableData, [user.ClientGroupSelected, user.refreshFlag]);
	useEffect(filterTableData, [schedulers, dataTableFilter]);
	useEffect(loadAutomationRunHistory, [showHistoricModal]);
	useEffect(loadAutomationAuditRecords, [showAuditModal]);
	useEffect(handleGroupChange, [group]);
	useEffect(handleTypeChange, [type]);
	useEffect(handleResourceOptionChange, [resourceOption]);
	useEffect(validateAutomationForm, [group, type, resourceOption, weekDays, hour, size]);

	const resSize = usingResize ? 7 : 11;

	return (
		<Layout pageTitle="Automações" functionsGeneric={functionsGeneric} gridFilter={gridFilter} dots={layoutDots}>

			<DialogConfirmForm
				onCloseEvent={deleteAutomation}
				open={showDeleteConfirmation}
				title="Deseja excluir essa automação?"
				content="Está operação NÃO PODE SER REVERTIDA caso a confirme!"
				confirmMessage="Sim, desejo excluir"
				abortMessage="Não, não desejo excluir"
			></DialogConfirmForm>

			<DialogConfirmForm
				onCloseEvent={toggleAutomationStatus}
				open={showToggleActiveConfirmation}
				title={`Deseja ${scheduler.status == 0 ? "ativar" : "desativar"} essa automação?`}
				content={`Esta automação no momento está ${scheduler.status == 0 ? "DESABILITADA" : "HABILITADA"}.`}
				confirmMessage={`Sim, desejo ${scheduler.status == 0 ? "ativar" : "desativar"}`}
				abortMessage={`Não, não desejo ${scheduler.status == 0 ? "ativar" : "desativar"}`}
			></DialogConfirmForm>

			<PPModal title="Detalhes" visible={showDetailsModal} onClose={() => setShowDetailsModal(false)} >
				{
					resource.resourceName !== "" ?
						<>
							<p className="detailRow"><strong>ID: </strong>
								{
									resource.resourceID.length > 60 ?
										<span>{resource.resourceID.substring(0, 59)}...</span> :
										resource.resourceID
								}
							</p>
							<p className="detailRow"><strong>Nome: </strong>{resource.resourceName}</p>
							<p className="detailRow"><strong>Provedor: </strong>{resource.resourceProvider}</p>
							<p className="detailRow"><strong>Conta/Assinatura: </strong>{resource.resourceSubscription}</p>
							<p className="detailRow"><strong>Região: </strong>{resource.resourceRegion}</p>
							{
								template.cloudOSId == 1 &&
								<p className="detailRow"><strong>Grupo: </strong>{resource.resourceGroup}</p>
							}
							<p className="detailRow"><strong>Tamanho: </strong>{resource.instanceSize}</p>
						</>
						:
						<div style={{ padding: '15px' }}>
							<LcNoData label="Não foram encontradas informações adicionais sobre este recurso." />
						</div>
				}
			</PPModal>

			<PPModal title="Histórico de Execuções" visible={showHistoricModal} onClose={toggleHistoricModal} functions={historicFunctions} size="large" >
				<LcLoading loading={isLoadingHistory} label="Carregando histórico..." >
					<LcInfiniteTable
						loading={isLoadingHistory}
						columns={historicColumns}
						rows={runHistory}
						size={runHistory.length}
						loadMore={(size?: number) => { }}
						onSortChange={onHistoricSortChange}
						density="high"
					/>
				</LcLoading>
			</PPModal>

			<PPModal title="Auditoria - Histórico de Edições" visible={showAuditModal} onClose={toggleAuditModal} functions={auditFunctions} size="large" >
				<LcLoading loading={isLoadingAudit} label="Carregando auditoria..." >
					<LcInfiniteTable
						loading={isLoadingAudit}
						columns={auditColumns}
						rows={auditRecords}
						size={auditRecords.length}
						loadMore={(size?: number) => { }}
						onSortChange={onAuditSortChange}
						density="high"
					/>
				</LcLoading>
			</PPModal>

			<PPModal title={`${scheduler.id! > 0 ? "Editar" : "Criar"} automação`} visible={showFormModal} onClose={toggleFormModal} functions={formFunctions} size='large' >
				<DialogConfirmForm
					onCloseEvent={acceptAutomationSuggestion}
					open={showCreateSuggestionConfirmation}
					title={`Deseja criar outra automação para Ligar o recurso?`}
					content={`
						Você escolheu uma automação para Desligar um recurso.
						Recomendamos que seja criada também uma automação para Ligar o recurso novamente.
						Gostaria de fazer isso após a criação desta automação?
					`}
					confirmMessage={`Sim, criar "Ligar" em seguida`}
					abortMessage="Não, criar apenas essa automação"
				></DialogConfirmForm>

				<PPModal title={'Solicitar agendamento de automação'} visible={showHeyhoTicketModal} onClose={toggleTicketModal} functions={ticketFunctions} >
					<div className="ticketSummary">
						<p>Ao clicar em 'Solicitar', será aberto um chamado para nossa equipe de suporte criar uma nova rotina de automação em seu ambiente cloud.<br />
							Se desejar revisar os dados informados, selecione 'Voltar'.</p>
						<p><strong>Observações adicionais (opcional):</strong></p>
						<p><PPTextArea name="ticketObs" value={heyhoTicketObs} onChange={(e) => setHeyhoTicketObs(e.target.value)} /></p>
					</div>
				</PPModal>

				<PPGrid container spacing={0} alignItems='flex-start'>
					<PPGrid item xs={6} sm={6} md={6} lg={6} xl={6}>
						<div className='titulo-form'>Grupo de automação</div>
						<RadioGroup row name="automationGroup" value={group} onChange={(e) => setGroup(e.target.value as "VM" | "DB")} >
							<FormControlLabel className="rdButton" value="VM" control={<Radio color="primary" />} label="Máquinas virtuais" disabled={formDisabled.group} />
							<FormControlLabel className="rdButton" value="DB" control={<Radio color="primary" />} label="Banco de dados" disabled={formDisabled.group} />
						</RadioGroup>
					</PPGrid>
					<PPGrid item xs={5} sm={5} md={5} lg={5} xl={5}>
						<div className='titulo-form' >Tipo de automação</div>
						<PPDropDown
							name='automationId'
							required={true}
							value={type}
							options={types}
							onChange={(opt) => setType(opt)}
							openTrigger='click'
							disabled={formDisabled.type}
						/>
					</PPGrid>
					<PPGrid item xs={resSize} sm={resSize} md={resSize} lg={resSize} xl={resSize}>
						<div className='titulo-form'>Recurso</div>
						<PPDropDown
							name='resource'
							required={true}
							value={resourceOption}
							options={resourceOptions}
							onChange={(opt) => setResourceOption(opt)}
							openTrigger='click'
							maxMenuHeight={100}
							disabled={formDisabled.resource}
						/>
					</PPGrid>
					{
						usingResize &&
						<PPGrid item xs={4} sm={4} md={4} lg={4} xl={4}>
							<div className='titulo-form'>Tamanho</div>
							<PPDropDown
								name='size'
								required={size !== undefined}
								value={size}
								options={sizes}
								onChange={(opt) => setSize(opt)}
								openTrigger='click'
								maxMenuHeight={100}
								disabled={formDisabled.size}
							/>
							<div style={{ display: resourceOption.value != "" && sizes.length == 0 ? "block" : "none", color: "red", fontSize: "12px" }}>
								Não foram encontrados tamanhos de instância na região desse recurso!
							</div>
						</PPGrid>
					}
					<PPGrid item xs={6} sm={6} md={6} lg={6} xl={6}>
						<div className='titulo-form'>Dias da semana</div>
						<div style={{ display: 'flex', justifyContent: 'center', padding: '5px' }}>
							<LcToggle title="Domingo" disabled={formDisabled.weekDay} toggled={weekDays.SUN} onChange={(e) => setWeekDays({ ...weekDays, SUN: e.checked })} />
							<LcToggle title="Segunda-feria" disabled={formDisabled.weekDay} toggled={weekDays.MON} onChange={(e) => setWeekDays({ ...weekDays, MON: e.checked })} />
							<LcToggle title="Terça-feira" disabled={formDisabled.weekDay} toggled={weekDays.TUE} onChange={(e) => setWeekDays({ ...weekDays, TUE: e.checked })} />
							<LcToggle title="Quarta-feira" disabled={formDisabled.weekDay} toggled={weekDays.WED} onChange={(e) => setWeekDays({ ...weekDays, WED: e.checked })} />
						</div>
						<div style={{ display: 'flex', justifyContent: 'center', padding: '5px' }}>
							<LcToggle title="Quinta-feira" disabled={formDisabled.weekDay} toggled={weekDays.THU} onChange={(e) => setWeekDays({ ...weekDays, THU: e.checked })} />
							<LcToggle title="Sexta-feira" disabled={formDisabled.weekDay} toggled={weekDays.FRI} onChange={(e) => setWeekDays({ ...weekDays, FRI: e.checked })} />
							<LcToggle title="Sábado" disabled={formDisabled.weekDay} toggled={weekDays.SAT} onChange={(e) => setWeekDays({ ...weekDays, SAT: e.checked })} />
						</div>
					</PPGrid>
					<PPGrid item xs={2} sm={2} md={2} lg={2} xl={2}>
						<div className='titulo-form'>Horário de início</div>
						<PPDropDown
							name='hourScheduled-hour'
							required={true}
							options={hourScheduledOptions.hours}
							value={{
								label: hour.hour,
								value: hour.hour
							}}
							onChange={(opt) => setHour({ ...hour, hour: opt.value })}
							openTrigger='click'
							menuPlacement='top'
							maxMenuHeight={100}
							disabled={formDisabled.hour}
						/>
					</PPGrid>
					<PPGrid item xs={1} sm={1} md={1} lg={1} xl={1}>
						<div className='titulo-form'></div>
						:
					</PPGrid>
					<PPGrid item xs={2} sm={2} md={2} lg={2} xl={2}>
						<div className='titulo-form'></div>
						<PPDropDown
							name='hourScheduled-minute'
							required={true}
							options={hourScheduledOptions.minutes}
							value={{
								label: hour.minute,
								value: hour.minute
							}}
							onChange={(opt) => setHour({ ...hour, minute: opt.value })}
							openTrigger='click'
							menuPlacement='top'
							maxMenuHeight={100}
							disabled={formDisabled.hour}
						/>
					</PPGrid>
				</PPGrid>
			</PPModal>

			<LcLoading loading={isLoading} label="Carregando automações...">
				<div className="page-automacoes">
					<LcInfiniteTable
						loading={isLoading}
						columns={columns}
						rows={filteredSchedulers}
						size={filteredSchedulers.length}
						filter={tableFilter}
						hidePagination={true}
						functionsRow={functionsRow}
						loadMore={(size?: number) => { }}
						onSortChange={onAutomationsSortChange}
						density={pref.data?.density || "medium"}
					/>
				</div>
			</LcLoading>
		</Layout>
	)
}

export default Automations;